// A visual's server PLAN → a ProPivot report, and the resulting cube → the
// `jsonData` every renderer already consumes.
//
// The dashboard ships one MODEL per data source (rows at a grain covering every
// visual on it). Each visual then folds that model itself: its plan names the
// dimensions, the series, and the measures with the aggregation to fold by
// (see backend/app/services/visual_model_service.py). ProPivot does the
// grouping — it is a columnar OLAP engine, so "GROUP BY dims, SUM(x)" is just a
// slice — and this module translates in and out of it.
//
// Two naming layers meet here and must not be confused:
//   * MODEL column  — the alias in the shared dataset (`AMOUNT__SUM`).
//   * VISUAL column — the name the visual's own query would have returned
//                     (`AMOUNT`), which is what its layout/renderers look up.
// The report is built over model columns; the output is re-keyed to visual
// columns, so nothing downstream can tell the data came from a shared model.

import type { VisualJsonData } from './types'

/** One dimension in a plan: the visual's column name + where it lives in the model. */
export interface PlanDimension {
  column: string
  modelColumn: string
}

/** One measure: how the visual folds a model column (or a derived pair). */
export interface PlanMeasure {
  column: string
  /** Fold applied to the model's partials: sum | min | max | average. */
  agg: string
  modelColumn?: string
  /** AVG is exact only as SUM/COUNT — the server ships both and we divide. */
  derive?: { sum: string; count: string }
}

/** What the server says about serving one visual from a model. */
export interface VisualPlan {
  supported: boolean
  reason?: string
  sourceKey?: string
  VISUAL_ID?: string
  dims?: PlanDimension[]
  series?: PlanDimension[]
  measures?: PlanMeasure[]
  /** The visual's own criteria, as a client-evaluable expression. */
  filter?: { condition: 'and' | 'or'; rules: Array<Record<string, unknown>> } | null
  topN?: { n: number } | null
  sort?: Array<{ column: string; direction: string }>
}

// The minimal shape of the ProPivot pieces used here (the library is aliased in
// each consumer's vite config; these keep this module type-safe without it).
export interface ProPivotAxisNode {
  path: string[]
  children: ProPivotAxisNode[]
  isLeaf: boolean
  expanded: boolean
}
export interface ProPivotMeasure { uniqueName: string; key: string; aggregation: string }
export interface ProPivotMatrix {
  rowTree: ProPivotAxisNode[]
  colTree: ProPivotAxisNode[]
  rowFields: string[]
  colFields: string[]
  measures: ProPivotMeasure[]
  cells: Map<string, number>
}

// Path separators ProPivot keys its cells with (matrix.ts US/GS).
const US = '␟'
const GS = '␞'

function cellKey(rowPath: string[], colPath: string[], measureKey: string): string {
  return `${rowPath.join(US)}${GS}${colPath.join(US)}${GS}${measureKey}`
}

/** Ordered leaf paths of an axis tree (mirrors ProPivot's own `leafPaths`). */
export function leafPaths(tree: ProPivotAxisNode[]): string[][] {
  const out: string[][] = []
  const walk = (nodes: ProPivotAxisNode[]) => {
    for (const node of nodes) {
      if (node.isLeaf || !node.expanded || !node.children.length) out.push(node.path)
      else walk(node.children)
    }
  }
  walk(tree)
  return out
}

/** Every model column a plan reads. */
export function planModelColumns(plan: VisualPlan): string[] {
  const out = new Set<string>()
  for (const dim of [...(plan.dims || []), ...(plan.series || [])]) {
    if (dim.modelColumn) out.add(dim.modelColumn)
  }
  for (const measure of plan.measures || []) {
    if (measure.modelColumn) out.add(measure.modelColumn)
    if (measure.derive) { out.add(measure.derive.sum); out.add(measure.derive.count) }
  }
  return [...out]
}

/**
 * Build the ProPivot report for one plan.
 *
 * Dimensions are declared `string` on purpose: the model's values arrive as the
 * database returned them (ISO dates, codes), and a date TYPE would re-format
 * members into display text — changing the very values a chart plots and a
 * filter matches. ISO strings still sort chronologically.
 */
export function planToReport(plan: VisualPlan, rows: Array<Record<string, unknown>>): unknown {
  const dims = plan.dims || []
  const series = plan.series || []
  const measures = plan.measures || []

  const mapping: Record<string, { type: string }> = {}
  for (const dim of [...dims, ...series]) mapping[dim.modelColumn] = { type: 'string' }

  // One ProPivot measure per model column the plan folds. A derived AVG
  // contributes its two partials; the division happens in matrixToJsonData.
  const reportMeasures: Array<{ uniqueName: string; aggregation: string }> = []
  const seen = new Set<string>()
  const addMeasure = (name: string, aggregation: string) => {
    const id = `${name}|${aggregation}`
    if (seen.has(id)) return
    seen.add(id)
    mapping[name] = { type: 'number' }
    reportMeasures.push({ uniqueName: name, aggregation })
  }
  for (const measure of measures) {
    if (measure.derive) {
      addMeasure(measure.derive.sum, 'sum')
      addMeasure(measure.derive.count, 'sum')
    } else if (measure.modelColumn) {
      addMeasure(measure.modelColumn, normalizeAgg(measure.agg))
    }
  }

  // Sort: by a measure when the plan sorts on one, else the dimension's own order.
  const sortEntry = (plan.sort || [])[0]
  const measureByColumn = new Map(measures.map((m) => [m.column, m]))
  let sorting: Record<string, unknown> | undefined
  const rowHierarchies: Array<Record<string, unknown>> = dims.map((dim) => ({
    uniqueName: dim.modelColumn,
  }))
  if (sortEntry) {
    const sortMeasure = measureByColumn.get(sortEntry.column)
    const direction = sortEntry.direction === 'desc' ? 'desc' : 'asc'
    if (sortMeasure) {
      const name = sortMeasure.derive ? sortMeasure.derive.sum : sortMeasure.modelColumn
      if (name) sorting = { row: { measure: name, type: direction } }
    } else {
      const dimIndex = dims.findIndex((d) => d.column === sortEntry.column)
      if (dimIndex >= 0) rowHierarchies[dimIndex].sort = direction
    }
  }

  // Top-N caps the ROW axis, ranked by the first measure — the same thing
  // SQLMODEL.ROW_LIMIT does to an ordered result.
  if (plan.topN && plan.topN.n > 0 && rowHierarchies.length) {
    const first = measures[0]
    const rankBy = first ? (first.derive ? first.derive.sum : first.modelColumn) : undefined
    if (rankBy) {
      rowHierarchies[0].filter = { type: 'top', measure: rankBy, quantity: plan.topN.n }
    }
  }

  return {
    dataSource: { type: 'json', data: rows, mapping },
    slice: {
      rows: rowHierarchies,
      columns: series.map((s) => ({ uniqueName: s.modelColumn })),
      measures: reportMeasures,
      // Fully expanded, or leafPaths would stop at the top level and the
      // visual would render one point per first-level member.
      expands: { expandAll: true },
      ...(sorting ? { sorting } : {}),
    },
    options: {
      grid: { showTotals: false, showGrandTotals: false },
    },
  }
}

// SQL aggregation → ProPivot aggregation. The server only ever asks for folds
// that are exact over partials.
function normalizeAgg(agg: string): string {
  const key = String(agg || 'sum').toLowerCase()
  if (key === 'avg' || key === 'average') return 'average'
  if (key === 'min' || key === 'max' || key === 'sum' || key === 'count') return key === 'count' ? 'sum' : key
  return 'sum'
}

/**
 * Cube → `[schemaRow, ...rows]`, keyed by the VISUAL's column names.
 *
 * One output row per (row leaf × column leaf) that has data, which is exactly
 * the long-format shape a visual's own grouped query returns — so charts,
 * natives, drill-down, underlying data and export all behave identically.
 */
/**
 * Is this measure a COUNT? Model aliases are `NAME__FUNCTION`, and COUNT folds
 * by summing partial counts — so the alias, not `agg`, is what distinguishes a
 * count from a sum. It decides only what an EMPTY population reports (0 vs null).
 */
function isCountMeasure(measure: PlanMeasure): boolean {
  if (measure.derive) return false     // an AVG over no rows is NULL
  return /__COUNT$/i.test(measure.modelColumn || '')
}

export function matrixToJsonData(matrix: ProPivotMatrix, plan: VisualPlan): VisualJsonData {
  const dims = plan.dims || []
  const series = plan.series || []
  const measures = plan.measures || []

  // Model column → the measure slot key cells are stored under.
  const slotByName = new Map<string, string>()
  for (const measure of matrix.measures || []) {
    if (!slotByName.has(measure.uniqueName)) slotByName.set(measure.uniqueName, measure.key)
  }
  const cellAt = (rowPath: string[], colPath: string[], modelColumn?: string): number => {
    if (!modelColumn) return NaN
    const slot = slotByName.get(modelColumn)
    if (!slot) return NaN
    const value = matrix.cells.get(cellKey(rowPath, colPath, slot))
    return value === undefined ? NaN : value
  }

  // Axis field → the plan entry that produced it, so members re-key to the
  // visual's own column names.
  const dimByModel = new Map([...dims, ...series].map((d) => [d.modelColumn, d]))

  const rowPaths = leafPaths(matrix.rowTree || [])
  const colPaths = leafPaths(matrix.colTree || [])
  const rowList = rowPaths.length ? rowPaths : [[]]
  const colList = colPaths.length ? colPaths : [[]]

  const out: Array<Record<string, unknown>> = []
  for (const rowPath of rowList) {
    for (const colPath of colList) {
      const row: Record<string, unknown> = {}
      ;(matrix.rowFields || []).forEach((field, i) => {
        const dim = dimByModel.get(field)
        if (dim && rowPath[i] !== undefined) row[dim.column] = rowPath[i]
      })
      ;(matrix.colFields || []).forEach((field, i) => {
        const dim = dimByModel.get(field)
        if (dim && colPath[i] !== undefined) row[dim.column] = colPath[i]
      })
      let hasValue = false
      for (const measure of measures) {
        let value: number
        if (measure.derive) {
          const sum = cellAt(rowPath, colPath, measure.derive.sum)
          const count = cellAt(rowPath, colPath, measure.derive.count)
          // AVG folded exactly: total ÷ population, never a mean of means.
          value = Number.isFinite(sum) && Number.isFinite(count) && count !== 0 ? sum / count : NaN
        } else {
          value = cellAt(rowPath, colPath, measure.modelColumn)
        }
        if (Number.isFinite(value)) hasValue = true
        row[measure.column] = Number.isFinite(value) ? value : null
      }
      // A (row × column) pair with no measure at all is an empty cross-tab cell,
      // not a data point — emitting it would draw a phantom category. A plan
      // with NO dimensions is the exception: it has exactly one cell and that
      // cell IS the answer, so an empty source must still report the grand
      // total the visual's own ungrouped query would return (SQL yields one row
      // for `SELECT COUNT(*) …` with no GROUP BY, never zero) — otherwise a KPI
      // over an empty table renders blank where it should read 0.
      const grandTotal = !dims.length && !series.length
      if (hasValue || !measures.length || grandTotal) {
        if (!hasValue && grandTotal) {
          for (const measure of measures) {
            // COUNT over no rows is 0; SUM/MIN/MAX/AVG over no rows is NULL.
            row[measure.column] = isCountMeasure(measure) ? 0 : null
          }
        }
        out.push(row)
      }
    }
  }

  const schema: Record<string, unknown> = { id: 0 }
  for (const dim of [...dims, ...series]) schema[dim.column] = { type: 'string' }
  for (const measure of measures) schema[measure.column] = { type: 'number' }

  return [schema, ...out.map((row, i) => ({ id: i + 1, ...row }))] as unknown as VisualJsonData
}
